import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, MailCheck, RotateCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export function LoginOtpStep({ challenge, onDone, onBack }) {
  const { verifyOtp } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(challenge.resend_cooldown || 60);
  const [masked, setMasked] = useState(challenge.email_masked);

  useEffect(() => {
    if (wait <= 0) { return undefined; }
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try { await verifyOtp(challenge.challenge_id, code); onDone(); }
    catch (err) { toast.error(apiErr(err)); setCode(""); if (err.response?.data?.detail?.includes("Reconnectez")) onBack(); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    try { const r = await api.post("/auth/login/resend", { challenge_id: challenge.challenge_id }); setMasked(r.data.email_masked); setWait(60); toast.success("Nouveau code envoyé"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="otp-step">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
        <MailCheck className="h-6 w-6 text-emerald-700 shrink-0" />
        <p className="text-sm text-slate-700">Nouvel appareil détecté. Un code à 6 chiffres a été envoyé à <b data-testid="otp-email">{masked}</b>. Il est valable {challenge.expires_minutes || 10} minutes.</p>
      </div>
      <div className="flex justify-center">
        <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={() => setTimeout(() => document.getElementById("otp-submit")?.click(), 0)} inputMode="numeric" pattern="^[0-9]*$" autoFocus data-testid="otp-input">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg font-mono" />)}
          </InputOTPGroup>
        </InputOTP>
      </div>
      <Button id="otp-submit" type="submit" disabled={busy || code.length !== 6} data-testid="otp-submit" className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white font-semibold">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vérifier et se connecter"}
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-slate-700" data-testid="otp-back"><ArrowLeft className="h-4 w-4" />Retour</button>
        <button type="button" onClick={resend} disabled={wait > 0} className="flex items-center gap-1 text-brand-700 hover:underline disabled:text-slate-400 disabled:no-underline" data-testid="otp-resend">
          <RotateCw className="h-4 w-4" />{wait > 0 ? `Renvoyer le code (${wait} s)` : "Renvoyer le code"}
        </button>
      </div>
    </form>
  );
}
